/**
 * DepartmentSchedules — assign a default timetable or shift cycle to
 * each department. Migrated to live API in Wave 3.2.
 *
 * The backend has one Schedule row per (department, type) assignment;
 * this page flattens the list to one row per department, showing the
 * active (non-expired) schedule as the "current" assignment. Departments
 * without a schedule show up as Unassigned so the operator can pick
 * one without navigating elsewhere.
 *
 * Parallel fetches on mount to build the lookup maps the table needs:
 *   - departmentsApi.list()             → rows
 *   - schedulesApi.listDepartmentSchedules() → current assignments
 *   - timetablesApi.list()              → resolve timetable_id → name
 *   - shiftCyclesApi.list()             → resolve shift_cycle_id → name
 *
 * PUT /v1/attendance/schedules/departments/{dept_id} creates a fresh
 * Schedule row per assignment (the backend doesn't upsert — each call
 * is a new row; the "current" one is the most recent). This matches
 * how the backend models history: assignments are append-only.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, FormControlLabel, IconButton,
  InputLabel, MenuItem, Paper, Select, Snackbar, Stack, Switch, Table,
  TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import {
  CloseOutlined, EditOutlined, RefreshOutlined,
} from '@mui/icons-material';
import { ApiError, attendanceApi, organizationApi } from '../../../api';
import type {
  DeptScheduleAssign, Schedule, ShiftCycle, Timetable,
} from '../../../api/attendance';
import type { DepartmentNode } from '../../../api/organization';
import { useTranslation } from 'react-i18next';

import DateField from '../../../components/inputs/DateField';
import { translateApiError } from '../../../i18n/translateApiError';
// ---------------------------------------------------------------------------
// Row shape for the flattened table
// ---------------------------------------------------------------------------

interface DeptRow {
  id: string;
  name: string;
  worker_count: number;
  /** Active schedule row for this dept, if any. */
  schedule?: Schedule;
  /** Resolved name ('Standard Day' or 'Three Shift Rotation' or null). */
  scheduleLabel: string;
  /** 'Fixed' | 'Cycle' | null. */
  scheduleKind: 'fixed' | 'cycle' | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const DepartmentSchedules: React.FC = () => {
  const { t } = useTranslation();
  const [depts, setDepts] = useState<DepartmentNode[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [timetables, setTimetables] = useState<Timetable[]>([]);
  const [cycles, setCycles] = useState<ShiftCycle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Deep-link support (Departments → Department schedules kebab). When
  // /attendance/schedules/department?department_id=X lands, we open the
  // edit dialog for that dept once data loads.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkDeptId = searchParams.get('department_id');

  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editing, setEditing] = useState<DeptRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<{
    kind: 'fixed' | 'cycle';
    timetable_id: string;
    shift_cycle_id: string;
    effective_from: string;
    auto_shift: boolean;
  }>({
    kind: 'fixed',
    timetable_id: '',
    shift_cycle_id: '',
    effective_from: new Date().toISOString().slice(0, 10),
    auto_shift: false,
  });

  // ---------- data ----------
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, s, t, c] = await Promise.all([
        organizationApi.departmentsApi.list(),
        attendanceApi.schedulesApi.listDepartmentSchedules(),
        attendanceApi.timetablesApi.list(),
        attendanceApi.shiftCyclesApi.list(),
      ]);
      setDepts(d);
      setSchedules(s);
      setTimetables(t);
      setCycles(c);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:deptSchedules.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ---------- lookups ----------
  const timetableById = useMemo(
    () => new Map(timetables.map((t) => [t.id, t])),
    [timetables],
  );
  const cycleById = useMemo(
    () => new Map(cycles.map((c) => [c.id, c])),
    [cycles],
  );

  // Pick the most-recent active schedule per department. Schedules are
  // append-only so the last one wins.
  const activeByDept = useMemo(() => {
    const map = new Map<string, Schedule>();
    for (const s of schedules) {
      if (!s.department_id) continue;
      if (s.status !== 'active') continue;
      const prev = map.get(s.department_id);
      if (!prev || new Date(s.created_at) > new Date(prev.created_at)) {
        map.set(s.department_id, s);
      }
    }
    return map;
  }, [schedules]);

  const rows: DeptRow[] = useMemo(() => {
    const list: DeptRow[] = [];
    const walk = (nodes: DepartmentNode[]) => {
      for (const d of nodes) {
        const s = activeByDept.get(d.id);
        let label = 'Unassigned';
        let kind: 'fixed' | 'cycle' | null = null;
        if (s) {
          if (s.timetable_id && timetableById.has(s.timetable_id)) {
            label = timetableById.get(s.timetable_id)!.name;
            kind = 'fixed';
          } else if (s.shift_cycle_id && cycleById.has(s.shift_cycle_id)) {
            label = cycleById.get(s.shift_cycle_id)!.name;
            kind = 'cycle';
          } else {
            label = `(Schedule row ${s.id})`;
            kind = (s.type === 'cycle' ? 'cycle' : 'fixed');
          }
        }
        list.push({
          id: d.id,
          name: d.name,
          worker_count: d.worker_count ?? 0,
          schedule: s,
          scheduleLabel: label,
          scheduleKind: kind,
        });
        if (d.children && d.children.length > 0) walk(d.children);
      }
    };
    walk(depts);
    return list;
  }, [depts, activeByDept, timetableById, cycleById]);

  // Deep-link: auto-open the edit dialog for ?department_id=X. Waits
  // until rows + timetables + cycles are all populated so the dialog
  // comes up with fully-hydrated state (matches the kebab menu's
  // expectation that clicking lands you ready to edit).
  useEffect(() => {
    if (!deepLinkDeptId || editing || rows.length === 0) return;
    const target = rows.find((r) => r.id === deepLinkDeptId);
    if (!target) return;
    handleOpenEdit(target);
    const next = new URLSearchParams(searchParams);
    next.delete('department_id');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkDeptId, editing, rows]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(q));
  }, [rows, filter]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  // ---------- mutations ----------
  const handleOpenEdit = (dept: DeptRow) => {
    setEditing(dept);
    setForm({
      kind: dept.scheduleKind ?? 'fixed',
      timetable_id: dept.schedule?.timetable_id ?? '',
      shift_cycle_id: dept.schedule?.shift_cycle_id ?? '',
      effective_from:
        (dept.schedule?.effective_from as string | undefined) ??
        new Date().toISOString().slice(0, 10),
      auto_shift: dept.schedule?.auto_shift ?? false,
    });
  };

  const canSave =
    !!editing &&
    ((form.kind === 'fixed' && !!form.timetable_id) ||
      (form.kind === 'cycle' && !!form.shift_cycle_id)) &&
    !!form.effective_from;

  const handleSave = async () => {
    if (!editing || !canSave) return;
    setSaving(true);
    try {
      const payload: DeptScheduleAssign = {
        type: form.kind,
        effective_from: form.effective_from,
        auto_shift: form.auto_shift,
        timetable_id: form.kind === 'fixed' ? form.timetable_id : null,
        shift_cycle_id: form.kind === 'cycle' ? form.shift_cycle_id : null,
      };
      await attendanceApi.schedulesApi.assignDepartmentSchedule(editing.id, payload);
      setToast(t('attendance:deptSchedules.toastUpdated', { name: editing.name }));
      setEditing(null);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      {/* Header */}
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5, mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>{t('attendance:deptSchedules.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('attendance:deptSchedules.subtitle')}</Typography>
          {/* How this layer interacts with the others.
              Same shape the Rules and Geofences pages already use — a second line of
              small text under the subtitle, not a diagram or a dialog. Added 2026-08-10
              on owner review: those two modules explain their override order and the
              three schedule pages explained nothing, while the schedule chain is the one
              with the surprises. Each sentence answers a specific way an admin gets it
              wrong, and no page repeats the whole chain (there is no GLOBAL schedule layer, unlike rules, so an admin arriving from Rules expects a fallback that does not exist). */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {t('attendance:deptSchedules.precedence')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton size="small" onClick={fetchAll} disabled={loading}
              sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}>
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <TextField size="small" variant="outlined" label={t('attendance:deptSchedules.filterLabel', { defaultValue: 'Filter Departments' })} value={filter}
          onChange={(e) => { setFilter(e.target.value); setPage(0); }}
          sx={{ width: 400, maxWidth: '100%', mr: 2 }} />
        <TablePagination component="div" count={filtered.length} page={page}
          onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]} />
      </Box>

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
            <TableRow>
              <TableCell>{t("attendance:deptSchedules.colDepartment")}</TableCell>
              <TableCell>{t('attendance:deptSchedules.colCurrent', { defaultValue: 'Current Schedule' })}</TableCell>
              <TableCell>{t('attendance:deptSchedules.colType', { defaultValue: 'Type' })}</TableCell>
              <TableCell align="center">{t('attendance:deptSchedules.colWorkers', { defaultValue: 'Workers' })}</TableCell>
              <TableCell align="center">{t('attendance:deptSchedules.colAutoShift', { defaultValue: 'Auto Shift' })}</TableCell>
              <TableCell align="center">{t("attendance:deptSchedules.colActions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  <Typography variant="body2">{t('attendance:deptSchedules.noMatch')}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((dept) => (
                <TableRow key={dept.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {dept.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {dept.schedule ? (
                      dept.scheduleLabel
                    ) : (
                      <Typography variant="caption" color="text.disabled">{t('attendance:deptSchedules.unassigned')}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {dept.scheduleKind ? (
                      <Chip label={dept.scheduleKind === 'cycle' ? 'Cycle' : 'Fixed'} size="small"
                        color={dept.scheduleKind === 'cycle' ? 'primary' : 'default'} variant="outlined" />
                    ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell align="center">{dept.worker_count}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('attendance:deptSchedules.autoShiftTooltip')}>
                      <Switch size="small" checked={dept.schedule?.auto_shift ?? false} disabled />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('common:actions.edit')}>
                      <IconButton size="small" onClick={() => handleOpenEdit(dept)}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
        {t('attendance:deptSchedules.dontSeeDepartment')}{' '}
        <Typography component="span" variant="caption"
          sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
          onClick={() => (window.location.href = '/organization/departments')}>
          {t('attendance:deptSchedules.manageDepartments')}
        </Typography>
      </Typography>

      {/* Edit dialog */}
      <Dialog open={!!editing} onClose={() => !saving && setEditing(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ m: 0, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary' }}>
            {t('attendance:deptSchedules.editTitle', { name: editing?.name })}
          </Typography>
          <IconButton size="small" onClick={() => !saving && setEditing(null)}>
            <CloseOutlined fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={1.75} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("attendance:deptSchedules.scheduleKindLabel")}</InputLabel>
              <Select value={form.kind} label={t("attendance:deptSchedules.scheduleKindLabel")}
                onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value as 'fixed' | 'cycle' }))}>
                <MenuItem value="fixed">{t("attendance:workerSchedules.fixedTimetable")}</MenuItem>
                <MenuItem value="cycle">{t("attendance:workerSchedules.shiftCycle")}</MenuItem>
              </Select>
            </FormControl>

            {form.kind === 'fixed' ? (
              <FormControl fullWidth size="small" disabled={timetables.length === 0}>
                <InputLabel>{t("attendance:workerSchedules.timetableLabel")}</InputLabel>
                <Select value={form.timetable_id} label={t("attendance:workerSchedules.timetableLabel")}
                  onChange={(e) => setForm((f) => ({ ...f, timetable_id: e.target.value }))}>
                  {timetables.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <FormControl fullWidth size="small" disabled={cycles.length === 0}>
                <InputLabel>{t("attendance:workerSchedules.shiftCycleLabel")}</InputLabel>
                <Select value={form.shift_cycle_id} label={t("attendance:workerSchedules.shiftCycleLabel")}
                  onChange={(e) => setForm((f) => ({ ...f, shift_cycle_id: e.target.value }))}>
                  {cycles.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <DateField fullWidth size="small" label={t("attendance:deptSchedules.effectiveFromLabel")}
              value={form.effective_from}
              onChange={(e) => setForm((f) => ({ ...f, effective_from: e.target.value }))} />

            {form.kind === 'cycle' && (
              <FormControlLabel control={
                <Switch size="small" checked={form.auto_shift}
                  onChange={(e) => setForm((f) => ({ ...f, auto_shift: e.target.checked }))} />
              } label={<Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{t('attendance:deptSchedules.autoAssignShifts')}</Typography>} />
            )}

            {timetables.length === 0 && form.kind === 'fixed' && (
              <Alert severity="info" variant="outlined" sx={{ fontSize: '0.75rem' }}>
                {t('attendance:deptSchedules.noTimetablesYet')}
              </Alert>
            )}
            {cycles.length === 0 && form.kind === 'cycle' && (
              <Alert severity="info" variant="outlined" sx={{ fontSize: '0.75rem' }}>
                No shift cycles yet. Create one in Shifts → Shift Cycles first.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button size="small" onClick={() => setEditing(null)} disabled={saving}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="contained" size="small" onClick={handleSave} disabled={!canSave || saving}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>
            {saving ? <CircularProgress size={18} /> : t('common:actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default DepartmentSchedules;
